/* eslint-disable @typescript-eslint/no-empty-function */
import {
  FloatingFocusManager,
  FloatingOverlay,
  FloatingPortal,
  useClick,
  useDismiss,
  useFloating,
  useInteractions,
  useRole,
} from '@floating-ui/react';
import classNames from 'classnames';
import { FC, useEffect, useRef, useState } from 'react';
import Icon from '../Icons/Icon';
import styles from './Modal.module.css';
import { ModalProps } from './Modal.types';

/**
 * The most basic/flexible modal - only has 3 required props:
 * @param isOpen {boolean} Required- controls if the modal is visible
 * @param onClose {() => void} Required - called when the close button is clicked; should close the modal
 * @param headerContent {ReactNode} Required - title displayed at the top of the modal
 * @param showTitleDivider {boolean} Optional - controls if the title divider
 * @param bodyContent {ReactNode} Optional - body displayed at the center of the modal
 * @param footerContent {ReactNode} Optional - footer displayed at the bottom of the modal on a grey background
 * @returns JSX (Portal)
 */

const Modal: FC<ModalProps> = ({
  isOpen,
  onClose,
  footerContent,
  headerContent,
  showTitleDivider,
  bodyContent,
  bodyClass,
  className,
  maxWidth,
  ...props
}) => {
  const closeButtonRef = useRef(null);
  const { floating, context } = useFloating({
    open: isOpen,
    onOpenChange: onClose,
  });

  const click = useClick(context);
  const role = useRole(context);
  const dismiss = useDismiss(context, { outsidePressEvent: 'mousedown' });

  const { getFloatingProps } = useInteractions([click, role, dismiss]);

  return (
    <FloatingPortal>
      {isOpen && (
        <FloatingOverlay
          className={styles.overlay}
          lockScroll
          onPointerEnterCapture={() => {}}
          onPointerLeaveCapture={() => {}}
        >
          <FloatingFocusManager context={context} initialFocus={closeButtonRef}>
            <div
              style={{ maxWidth }}
              className={classNames(styles.modal, { [className!]: className !== undefined })}
              ref={floating}
              data-testid="Modal"
              {...getFloatingProps()}
              {...props}
            >
              <div className={classNames(styles.title, { [styles.titleText]: typeof headerContent === 'string' })}>
                {headerContent}
              </div>
              {showTitleDivider && <div className={styles.divider} data-testid="divider" />}
              {bodyContent !== undefined && (
                <div className={classNames(styles.body, { [bodyClass!]: bodyClass !== undefined })}>{bodyContent}</div>
              )}
              {footerContent !== undefined && <div className={styles.footer}>{footerContent}</div>}
              {onClose && (
                <Icon
                  iconName="X"
                  data-testid="closeModalButton"
                  onClick={onClose}
                  className={styles.closeIcon}
                  ref={closeButtonRef}
                  title="Close"
                />
              )}
            </div>
          </FloatingFocusManager>
        </FloatingOverlay>
      )}
    </FloatingPortal>
  );
};

export default Modal;
